import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Loader } from '../ui/Loader';
import { useGetProxyServersQuery, useGetDisabledServersQuery } from '../../store/api/proxiesApi';

const SERVER_ID_MAP: Record<string, number> = {
  server_A: 1,
  server_B: 2,
  server_C: 3,
  server_D: 4,
  server_E: 5,
  proxygb: 5,
  "5": 5,
};

const PROXY_SERVER_TO_KEY: Record<number, string> = {
  1: "proxy_cheap",
  2: "proxyline",
  3: "proxylte",
  4: "proxy5g",
  5: "proxygb",
};

function resolveServerNum(server: { id?: string; name?: string }): number {
  const raw = String(server?.id || "").trim();
  if (SERVER_ID_MAP[raw]) return SERVER_ID_MAP[raw];
  const asNum = Number(raw);
  if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 5) return asNum;
  const name = String(server?.name || "").toLowerCase();
  if (name.includes("residential") || name.includes("gb") || name.includes("باك")) return 5;
  if (name.includes("5g") || name.includes("rotating mobile")) return 4;
  return 0;
}

export function ServerSelection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetProxyServersQuery();
  const { data: disabledData } = useGetDisabledServersQuery();

  const disabledServers: string[] = useMemo(
    () => disabledData?.data?.disabledServers || [],
    [disabledData],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  if (error || !data?.data?.servers?.length) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-medium font-body">
          {t('proxies.purchase.noServers')}
        </p>
      </div>
    );
  }

  const servers = data.data.servers;

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
      {servers.map((server) => {
        const serverNum = resolveServerNum(server);
        const isDisabled = disabledServers.includes(PROXY_SERVER_TO_KEY[serverNum]);
        return (
          <button
            key={server.id}
            onClick={() => {
              if (isDisabled) return;
              if (!serverNum) return;
              navigate(`/proxies/purchase/${serverNum}`);
            }}
            className={`relative bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-all duration-300 text-left ${
              isDisabled
                ? 'border-red-500/30 opacity-60 cursor-not-allowed'
                : 'border-neutral-dark hover:border-brand-accent/50 transform hover:scale-105 cursor-pointer'
            }`}
          >
            {isDisabled && (
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                <span className="bg-red-500/90 text-white text-[10px] font-heading font-bold px-3 py-1 rounded-full shadow-lg shadow-red-500/30 whitespace-nowrap">
                  متوقف - تحت الصيانة
                </span>
              </div>
            )}
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isDisabled ? 'bg-red-500/10' : 'bg-brand-accent/20'
              }`}>
                <svg
                  className={`w-8 h-8 ${isDisabled ? 'text-red-400' : 'text-brand-accent'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                  />
                </svg>
              </div>
              <h3 className={`text-xl font-heading font-semibold mb-2 ${
                isDisabled ? 'text-red-400' : 'text-neutral-light'
              }`}>
                {server.name || t(`proxies.server.server${serverNum}`)}
              </h3>
              <p className="text-neutral-medium font-body text-sm">
                {server.description || t(`proxies.server.server${serverNum}Description`)}
              </p>
            </div>
            <div className="text-center">
              {isDisabled ? (
                <span className="text-red-400 font-body font-medium text-sm flex items-center justify-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  السيرفر متوقف
                </span>
              ) : (
                <span className="text-brand-accent font-body font-medium">
                  {t('proxies.purchase.selectServer')} →
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
